import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { MACHINES, DELIVERY, PAYMENTS, fmt, calculate } from "@/lib/pricing";
import { usePricing } from "@/hooks/usePricing";
import { trackCalculatorUse, trackLeadSubmit } from "@/utils/analytics";
import { getLeadSource } from "@/utils/leadSource";
import { Minus, Plus, CheckCircle2, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OptGroup = ({ title, options, value, onChange, testPrefix, cols = "grid-cols-2" }) => (
  <div>
    <p className="eyebrow text-[#8c8882] mb-3">{title}</p>
    <div className={`grid ${cols} gap-3`}>
      {Object.entries(options).map(([key, o]) => (
        <button
          key={key}
          type="button"
          data-testid={`${testPrefix}-${key}`}
          onClick={() => onChange(key)}
          className={`opt-card text-left rounded-xl border px-4 py-3.5 ${
            value === key
              ? "border-[var(--terra)] bg-[var(--terra)]/10"
              : "border-[var(--line-dark)] bg-[#1c1f23] hover:border-[#4a4e55]"
          }`}
        >
          <p className={`text-sm font-semibold ${value === key ? "text-[var(--terra)]" : "text-[#faf8f5]"}`}>
            {o.label}
          </p>
          {o.sub && <p className="text-xs text-[#8c8882] mt-0.5">{o.sub}</p>}
        </button>
      ))}
    </div>
  </div>
);

export const Calculator = () => {
  const [machine, setMachine] = useState("reformer");
  const [quantity, setQuantity] = useState(1);
  const [delivery, setDelivery] = useState("d96");
  const [payment, setPayment] = useState("lease_24");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const pricing = usePricing();
  const calc = useMemo(() => calculate(machine, quantity, delivery, payment, pricing), [machine, quantity, delivery, payment, pricing]);

  const calculatorTrackedRef = useRef(false);
  const registerCalculatorUse = () => {
    if (calculatorTrackedRef.current) return;
    calculatorTrackedRef.current = true;
    trackCalculatorUse();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    const eventId = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    const cookie = (n) => document.cookie.match(`(?:^|; )${n}=([^;]*)`)?.[1] || null;
    if (window.fbq) window.fbq("track", "Lead", {}, { eventID: eventId });
    try {
      const { data } = await axios.post(`${API}/leads`, {
        ...form,
        machine_type: machine,
        quantity,
        delivery,
        payment,
        event_id: eventId,
        fbp: cookie("_fbp"),
        fbc: cookie("_fbc"),
        page_url: window.location.href,
        source: getLeadSource(),
      });
      setResult(data);
      trackLeadSubmit(data?.calculation?.total ?? calc.total);
      toast.success("Kalkulációja elkészült – hamarosan felvesszük Önnel a kapcsolatot!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Hiba történt, kérjük ellenőrizze az adatokat.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kalkulator" className="relative bg-[var(--ink)] pt-24 lg:pt-32 pb-12 lg:pb-14 grain overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow text-[var(--terra)] mb-4">Árkalkulátor</p>
          <h2 className="font-serif-d font-medium text-2xl sm:text-3xl lg:text-4xl text-[#faf8f5] max-w-2xl leading-tight">
            Állítsa össze konstrukcióját
          </h2>
          <div
            className="mt-6 max-w-2xl bg-[var(--terra)]/10 border border-[var(--terra)]/50 rounded-2xl px-6 py-5 flex items-start gap-4"
            data-testid="calculator-limited-note"
          >
            <Flame className="w-6 h-6 text-[var(--terra)] shrink-0 mt-0.5" />
            <div>
              <p className="eyebrow text-[var(--terra)]">Limitált részletfizetési lehetőség</p>
              <p className="mt-1.5 text-sm md:text-base text-[#faf8f5] leading-relaxed">
                Cégünk összesen <span className="text-[var(--terra)] font-bold">50 gépre</span> biztosít
                részletfizetést. A konstrukció kizárólag a keret betöltéséig érhető el,
                biztosítsa helyét időben!
              </p>
            </div>
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          {result ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-14 max-w-2xl mx-auto bg-[#1c1f23] border border-[var(--line-dark)] rounded-3xl p-10 text-center"
              data-testid="calculator-success"
            >
              <CheckCircle2 className="w-12 h-12 text-[var(--terra)] mx-auto" />
              <h3 className="font-serif-d text-2xl text-[#faf8f5] mt-5">Köszönjük, {result.name}!</h3>
              <p className="text-[#b8b4ae] mt-3 text-sm leading-relaxed">
                Kalkulációját rögzítettük, kollégánk hamarosan felveszi Önnel a kapcsolatot
                a részletes, személyre szabott ajánlattal.
              </p>
              <div className="mt-8 text-left bg-[var(--ink)] rounded-2xl p-6 space-y-2.5 text-sm">
                <Row k="Gép" v={`${result.calculation.machine_label} × ${result.calculation.quantity} db`} />
                <Row k="Szállítás" v={result.calculation.delivery_label} />
                <Row k="Fizetés" v={result.calculation.payment_label} />
                <div className="border-t border-[var(--line-dark)] pt-3">
                  {result.calculation.monthly > 0 ? (
                    <>
                      <Row k="Kezdő befizetés" v={fmt(result.calculation.down_payment)} strong />
                      <Row k="Havi részlet" v={`${fmt(result.calculation.monthly)} / hó · ${result.calculation.lease_months} hónapon át`} strong />
                    </>
                  ) : (
                    <Row k="Végár" v={fmt(result.calculation.total)} strong />
                  )}
                </div>
              </div>
              <button
                data-testid="calculator-reset-btn"
                onClick={() => setResult(null)}
                className="mt-8 eyebrow text-[var(--terra)] hover:underline"
              >
                Új kalkuláció indítása
              </button>
            </motion.div>
          ) : (
            <motion.div key="form" className="mt-14 grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-7 space-y-8">
                <OptGroup title="1 · Gép típusa" options={MACHINES} value={machine} onChange={(k) => { registerCalculatorUse(); setMachine(k); }} testPrefix="calc-machine" />
                <div>
                  <p className="eyebrow text-[#8c8882] mb-3">2 · Darabszám</p>
                  <div className="inline-flex items-center gap-5 bg-[#1c1f23] border border-[var(--line-dark)] rounded-xl px-4 py-2.5">
                    <button type="button" data-testid="calc-qty-minus" onClick={() => { const q = Math.max(1, quantity - 1); registerCalculatorUse(); setQuantity(q); }} className="text-[#faf8f5] hover:text-[var(--terra)]">
                      <Minus className="w-4 h-4" />
                    </button>
                    <span data-testid="calc-qty-value" className="font-mono-d text-[#faf8f5] w-8 text-center">{quantity}</span>
                    <button type="button" data-testid="calc-qty-plus" onClick={() => { const q = Math.min(20, quantity + 1); registerCalculatorUse(); setQuantity(q); }} className="text-[#faf8f5] hover:text-[var(--terra)]">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <OptGroup title="3 · Szállítási idő" options={DELIVERY} value={delivery} onChange={(k) => { registerCalculatorUse(); setDelivery(k); }} testPrefix="calc-delivery" />
                <OptGroup title="4 · Fizetési konstrukció" options={PAYMENTS} value={payment} onChange={(k) => { registerCalculatorUse(); setPayment(k); }} testPrefix="calc-payment" cols="grid-cols-2 sm:grid-cols-4" />
              </div>

              <div className="lg:col-span-5 flex flex-col">
                <p className="eyebrow text-[#8c8882] mb-3">Az Ön kalkulációja</p>
                <div className="flex-1 flex flex-col bg-[#1c1f23] border border-[var(--line-dark)] rounded-3xl p-6">
                  <div className="space-y-2.5 text-sm">
                    <Row k="Gép" v={`${MACHINES[machine].label} × ${quantity} db`} />
                    <Row k="Szállítás" v={`${DELIVERY[delivery].label} · ${DELIVERY[delivery].sub}`} />
                    {calc.deliveryFee > 0 && <Row k="Expressz szállítási díj" v={`+ ${fmt(calc.deliveryFee)}`} />}
                  </div>
                  <div className="mt-4 pt-4 border-t border-[var(--line-dark)]">
                    {calc.months > 0 ? (
                      <>
                        <p className="eyebrow text-[#8c8882]">Kezdő befizetés</p>
                        <p data-testid="calc-down" className="font-serif-d font-semibold text-3xl text-[#faf8f5] mt-1">
                          {fmt(calc.downPayment)}
                        </p>
                        <p data-testid="calc-monthly" className="text-[var(--terra)] font-semibold mt-2">
                          + {fmt(calc.monthly)} / hó · {calc.months} hónapon át
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="eyebrow text-[#8c8882]">Végár</p>
                        <p data-testid="calc-total" className="font-serif-d font-semibold text-3xl text-[#faf8f5] mt-1">
                          {fmt(calc.total)}
                        </p>
                      </>
                    )}
                    <p className="text-[10px] text-[#8c8882] mt-2">Az árak nettó árak.</p>
                  </div>

                  <form onSubmit={submit} className="mt-auto pt-5 space-y-2.5">
                    <input required data-testid="lead-name-input" placeholder="Név *" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[var(--ink)] border border-[var(--line-dark)] rounded-xl px-4 py-2.5 text-sm text-[#faf8f5] placeholder-[#5c5a57] focus:border-[var(--terra)] outline-none transition-colors" />
                    <input required type="email" data-testid="lead-email-input" placeholder="E-mail cím *" value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-[var(--ink)] border border-[var(--line-dark)] rounded-xl px-4 py-2.5 text-sm text-[#faf8f5] placeholder-[#5c5a57] focus:border-[var(--terra)] outline-none transition-colors" />
                    <input required data-testid="lead-phone-input" placeholder="Telefonszám *" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-[var(--ink)] border border-[var(--line-dark)] rounded-xl px-4 py-2.5 text-sm text-[#faf8f5] placeholder-[#5c5a57] focus:border-[var(--terra)] outline-none transition-colors" />
                    <button type="submit" disabled={sending} data-testid="lead-submit-btn"
                      className="btn-terra w-full rounded-xl py-3.5 text-sm font-semibold disabled:opacity-60">
                      {sending ? "Küldés..." : "Kérem, vegyék fel velem a kapcsolatot →"}
                    </button>
                  </form>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const Row = ({ k, v, strong }) => (
  <div className="flex justify-between gap-4">
    <span className={strong ? "text-[#faf8f5] font-semibold" : "text-[#8c8882]"}>{k}</span>
    <span className={strong ? "text-[var(--terra)] font-semibold" : "text-[#d8d4ce]"}>{v}</span>
  </div>
);
